import { user } from 'ampersand-app'

import store from '@/redux/store'
import { ADMIN_ROLES, SETTINGS_RIGHTS, USER_RIGHTS } from './modules/base/util/constants'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'

// maps the flat userRights list to a Set once per list instead of on every check
let rightsCache = { source: null, set: new Set() }
const toRightSet = (userRights = []) => {
  if (rightsCache.source !== userRights) {
    rightsCache = { source: userRights, set: new Set(userRights) }
  }
  return rightsCache.set
}

/**
 * Checks whether the user is granted the given USER_RIGHTS permission(s).
 * A permission is a dot-path string (e.g. 'invoices.write'); the backend sends
 * `userInfo.userRights` as the flat list of the permissions the user holds.
 *
 * @param {string|Array.<string>} userRight one or many USER_RIGHTS values
 * @param {object} userInfo the current user info holding `userRights`
 * @returns {boolean}
 */
const checkAccessRights = (userRight, userInfo = null) => {
  if (!userRight || !userInfo) {
    return true
  }

  const rights = Array.isArray(userRight) ? userRight : [userRight]

  const hasAccess = rights.every((right) => toRightSet(userInfo.userRights).has(right))

  if (!hasAccess) {
    return false
  }

  // read-only lock: block write permissions while the account is locked,
  // except for settings permissions which stay editable.
  const readOnlyConfirmed = WebStorageService.getItem(WebStorageKey.READ_ONLY_CONFIRMED)
  const isWriteCheck = rights.some((right) => !right.endsWith('.read'))
  const isSettingsCheck = rights.every((right) => right.startsWith('settings.'))
  if ((readOnlyConfirmed || user?.allSubscriptionsLocked) && isWriteCheck && !isSettingsCheck) {
    return false
  }

  return true
}

/**
 * checks for a given subscription module in the user's details and returns a boolean if the user's current plan includes it
 * if nothing is passed in, a subscription for the module is not set
 *
 * @param {string} subscriptionModule
 * @returns boolean
 */
const checkModuleAccess = (subscriptionModule, defaultValue = true) => {
  if (!subscriptionModule?.length) { return defaultValue }

  const subscriptionModules = Array.isArray(subscriptionModule) ? subscriptionModule : [subscriptionModule]
  const { permission: { moduleAccess } } = store.getState()

  return subscriptionModules.reduce((result, current) =>
    result || Boolean(moduleAccess[current])
  , false)
}

/**
 * Maps a route to the USER_RIGHTS group ({ READ, WRITE }) that gates it.
 * Returns null for routes that are only gated by a subscription module.
 *
 * @param {string} route
 * @returns {{ READ: string, WRITE: string } | null}
 */
const mapRouteToUserRight = (route) => {
  if (typeof route !== 'string') return null
  const withoutParams = route.split('?')[0]
  const routeTokens = withoutParams.split('/').filter(i => i)
  const reporting = { READ: USER_RIGHTS.ACCOUNTING.REPORTING, WRITE: USER_RIGHTS.ACCOUNTING.REPORTING }
  const chartOfAccounts = { READ: USER_RIGHTS.ACCOUNTING.BANKING_CHART_OF_ACCOUNTS, WRITE: USER_RIGHTS.ACCOUNTING.BANKING_CHART_OF_ACCOUNTS }
  let rights = null
  switch (routeTokens[0]) {
    case 'documentPayment':
    case 'documentSignature':
      rights = null
      break
    case 'suppliers':
    case 'supplier':
      rights = USER_RIGHTS.SUPPLIERS
      break
    case 'customers':
    case 'customer':
      rights = USER_RIGHTS.CUSTOMERS
      break
    case 'articles':
    case 'article':
      rights = USER_RIGHTS.ARTICLES
      break
    case 'offers':
    case 'offer':
      rights = USER_RIGHTS.OFFERS
      break
    case 'orders':
    case 'order':
      rights = USER_RIGHTS.ORDERS
      break
    case 'deliverynotes':
    case 'deliverynote':
      rights = USER_RIGHTS.DELIVERY_NOTES
      break
    case 'invoices':
    case 'invoice':
    case 'manualcreditnotes':
    case 'manualcreditnote':
    case 'closinginvoice':
    case 'closinginvoices':
    case 'depositinvoice':
    case 'depositinvoices':
    case 'partialinvoices':
    case 'partialinvoice':
      rights = USER_RIGHTS.INVOICES
      break
    case 'recurringinvoices':
    case 'recurringinvoice':
      rights = USER_RIGHTS.RECURRING_INVOICES
      break
    case 'projects':
    case 'project':
      rights = USER_RIGHTS.PROJECTS
      break
    case 'timetrackings':
    case 'timetracking':
      rights = USER_RIGHTS.TRACKED_TIMES
      break
    case 'banking':
      rights = USER_RIGHTS.ACCOUNTING
      break
    case 'expenses':
    case 'expense':
      rights = USER_RIGHTS.EXPENSES
      break
    case 'revenues':
    case 'revenue':
      rights = USER_RIGHTS.REVENUES
      break
    case 'document-export':
      rights = USER_RIGHTS.ACCOUNTANT_EXPORT
      break
    case 'vatReturn':
      rights = USER_RIGHTS.VAT_RETURN
      break
    case 'documents':
    case 'document':
      rights = USER_RIGHTS.DOCUMENTS
      break
    case 'reporting':
      rights = reporting
      break
    case 'users':
      rights = USER_RIGHTS.MEMBERS
      break
    case 'bookings':
      rights = USER_RIGHTS.ACCOUNTING
      break
    case 'economicAsset':
    case 'economicAssets':
      rights = USER_RIGHTS.ECONOMIC_ASSETS
      break
    case 'client':
    case 'clients':
      rights = USER_RIGHTS.CLIENTS
      break
    case 'my-account':
      rights = SETTINGS_RIGHTS.ACCOUNT
      break
    case 'taxForms':
    case 'taxReports':
      rights = USER_RIGHTS.TAX_FORMS
      break
    case 'settings':
      switch (routeTokens[1]) {
        case 'payment-conditions':
          rights = SETTINGS_RIGHTS.PAY_CONDITION
          break
        case 'myaccount':
          rights = SETTINGS_RIGHTS.ACCOUNT
          break
        case 'mycompany':
        case 'myoffice':
          rights = SETTINGS_RIGHTS.COMPANY
          break
        case 'letter-paper':
          rights = SETTINGS_RIGHTS.LETTER_PAPER
          break
        case 'customer-center':
          rights = SETTINGS_RIGHTS.CUSTOMER_CENTER
          break
        case 'interfaces':
          rights = SETTINGS_RIGHTS.INTERFACES
          break
        case 'masterdata':
          rights = SETTINGS_RIGHTS.MASTERDATA
          break
        case 'customFields':
          rights = SETTINGS_RIGHTS.CUSTOM_FIELDS
          break
        case 'more-settings':
          rights = SETTINGS_RIGHTS.MISCELLANEOUS
          break
        case 'delivery-conditions':
          rights = SETTINGS_RIGHTS.DELIVERY_CONDITION
          break
        case 'event-log':
          rights = SETTINGS_RIGHTS.EVENT_LOG
          break
        case 'mailbox':
          rights = USER_RIGHTS.SETTINGS
          break
        case 'tax-settings':
          switch (routeTokens[2]) {
            case 'chart-of-accounts':
              rights = chartOfAccounts
          }
          break
      }
      break
    case 'purchases':
    case 'purchase':
      rights = USER_RIGHTS.PURCHASES
      break
    case 'letters':
    case 'letter':
      rights = USER_RIGHTS.LETTERS
      break
    case 'apps':
    case 'app':
      rights = USER_RIGHTS.APPS
      break
  }
  return rights
}

/**
 * @param {Array.<string>} allowedRoles
 * @returns {boolean}
 */
const checkIfRoleIsAllowed = (allowedRoles = [], userInfo) => {
  if (userInfo?.roles?.length) {
    return userInfo.roles.some((role) => allowedRoles.includes(role))
  }
  return allowedRoles.length === 0
}

const checkIsAdminRole = (userInfo) =>
  checkIfRoleIsAllowed(ADMIN_ROLES, userInfo)

/**
 * Resolves the { read, write } access booleans for a given route.
 *
 * @param {string} route
 * @param {object} userInfo
 * @returns {{ read: boolean, write: boolean }}
 */
const getRouteAccessRights = (route, userInfo) => {
  const rights = mapRouteToUserRight(route)
  return {
    read: checkAccessRights(rights?.READ, userInfo),
    write: checkAccessRights(rights?.WRITE, userInfo),
  }
}

export { mapRouteToUserRight }
export { getRouteAccessRights }
export { checkAccessRights }
export { checkModuleAccess }
export { checkIfRoleIsAllowed }
export { checkIsAdminRole }
